import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Collapse,
  Card,
  CardContent,
  InputAdornment,
  CircularProgress,
  FormControlLabel,
  Switch,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import {
  collection,
  addDoc,
  getDocs,
  query as firestoreQuery,
  orderBy,
  updateDoc,
  doc,
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useAuth } from '../../contexts/AuthContext';
import { useSelector, useDispatch } from 'react-redux';
import { setProjects } from '../../redux/slices/projectsSlice';
import { setSearchResults } from '../../redux/slices/searchSlice';
import LinearProgress from '@mui/material/LinearProgress';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { createProject } from '../../redux/slices/projectsSlice';
import { setCustomers } from '../../redux/slices/customersSlice';
import moment from 'moment';

function ProjectList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const dispatch = useDispatch();
  const { customers } = useSelector((state) => state.customers);
  const { projects = [] } = useSelector((state) => state.projects || {});
  const { query: searchQuery, results } = useSelector(
    (state) => state.search || {}
  );
  const { openProjectDialog, setOpenProjectDialog } = useOutletContext();
  const [subProjects, setSubProjects] = useState([]);
  const [timeLogs, setTimeLogs] = useState([]);
  const [openSubProjectDialog, setOpenSubProjectDialog] = useState(false);
  const [openTimeLogDialog, setOpenTimeLogDialog] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedSubProject, setSelectedSubProject] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [newProject, setNewProject] = useState({
    name: '',
    clientId: '',
    isNewClient: false,
    isInternal: false,
    newClientName: '',
  });
  const [newSubProject, setNewSubProject] = useState({
    name: '',
    projectId: '',
    description: '',
    budgetHours: 0,
  });
  const [newTimeLog, setNewTimeLog] = useState({
    projectId: '',
    subProjectId: '',
    hours: 0,
    date: new Date().toISOString().split('T')[0],
    description: '',
    isBillable: true,
  });
  const [openEditProjectDialog, setOpenEditProjectDialog] = useState(false);
  const [openEditSubProjectDialog, setOpenEditSubProjectDialog] =
    useState(false);
  const [openEditTimeLogDialog, setOpenEditTimeLogDialog] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [editingSubProject, setEditingSubProject] = useState(null);
  const [editingTimeLog, setEditingTimeLog] = useState(null);

  useEffect(() => {
    fetchProjects();
    fetchSubProjects();
    fetchTimeLogs();
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (searchQuery) {
      const filtered = projects.filter(
        (project) =>
          project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (project.clientName &&
            project.clientName
              .toLowerCase()
              .includes(searchQuery.toLowerCase()))
      );
      dispatch(setSearchResults({ module: 'projects', results: filtered }));
    } else {
      dispatch(setSearchResults({ module: 'projects', results: projects }));
    }
  }, [searchQuery, projects, dispatch]);

  const fetchProjects = async () => {
    try {
      const q = firestoreQuery(collection(db, 'projects'), orderBy('name'));
      const querySnapshot = await getDocs(q);
      const projectsData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      dispatch(setProjects(projectsData));
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const handleSearchChange = (e) => {
    dispatch(setSearchResults({ module: 'projects', results: projects }));
  };

  const fetchSubProjects = async () => {
    try {
      const q = firestoreQuery(collection(db, 'subProjects'), orderBy('name'));
      const querySnapshot = await getDocs(q);
      setSubProjects(
        querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
      );
    } catch (error) {
      console.error('Error fetching sub-projects:', error);
    }
  };

  const fetchTimeLogs = async () => {
    try {
      const q = firestoreQuery(
        collection(db, 'timeLogs'),
        orderBy('date', 'desc')
      );
      const querySnapshot = await getDocs(q);
      setTimeLogs(
        querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
      );
    } catch (error) {
      console.error('Error fetching time logs:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const q = firestoreQuery(collection(db, 'customers'), orderBy('name'));
      const querySnapshot = await getDocs(q);
      const customersData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      dispatch(setCustomers(customersData));
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const handleCreateProject = async () => {
    try {
      const projectData = {
        name: newProject.name,
        clientId: newProject.isInternal ? null : newProject.clientId,
        clientName: newProject.isInternal
          ? 'Internal'
          : newProject.newClientName,
        isInternal: newProject.isInternal,
        createdAt: new Date(),
        createdBy: currentUser?.uid || null,
      };

      await addDoc(collection(db, 'projects'), projectData);
      setOpenProjectDialog(false);
      setNewProject({
        name: '',
        clientId: '',
        isNewClient: false,
        isInternal: false,
        newClientName: '',
      });
      fetchProjects();
    } catch (error) {
      console.error('Error creating project:', error);
    }
  };

  const handleCloseProjectDialog = () => {
    setOpenProjectDialog(false);
    setNewProject({
      name: '',
      clientId: '',
      isNewClient: false,
      isInternal: false,
      newClientName: '',
    });
  };

  const handleProjectSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      let projectName;
      let clientId;
      let clientName;

      if (newProject.isNewClient) {
        projectName = newProject.name;
        clientName = newProject.newClientName;
        clientId = newProject.clientId;
      } else if (newProject.isInternal) {
        projectName = newProject.name;
        clientName = 'Internal';
        clientId = null;
      } else {
        const selectedClient = customers.find(
          (c) => c.id === newProject.clientId
        );
        projectName = selectedClient?.name || '';
        clientName = selectedClient?.name || '';
        clientId = newProject.clientId;
      }

      await dispatch(
        createProject({
          name: projectName,
          clientId,
          clientName,
          isInternal: newProject.isInternal,
          createdAt: new Date(),
          createdBy: currentUser?.uid || null, // Add null fallback if currentUser is undefined
        })
      );
      handleCloseProjectDialog();
    } catch (error) {
      console.error('Error creating project:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateSubProject = async () => {
    try {
      await addDoc(collection(db, 'subProjects'), {
        name: newSubProject.name,
        projectId: newSubProject.projectId,
        description: newSubProject.description,
        budgetHours: newSubProject.budgetHours,
        createdAt: new Date(),
        createdBy: currentUser?.uid || null,
      });
      setOpenSubProjectDialog(false);
      setNewSubProject({
        name: '',
        projectId: '',
        description: '',
        budgetHours: 0,
      });
      fetchSubProjects();
    } catch (error) {
      console.error('Error creating sub-project:', error);
    }
  };

  const handleCreateTimeLog = async () => {
    try {
      // Ensure hours is stored as a number
      const hours = parseFloat(newTimeLog.hours);

      // Fix timezone issues by creating a date that preserves the selected date
      // Set time to noon to avoid timezone shifts across date boundaries
      const dateStr = newTimeLog.date;
      const [year, month, day] = dateStr
        .split('-')
        .map((num) => parseInt(num, 10));
      const dateObj = new Date(year, month - 1, day, 12, 0, 0);

      await addDoc(collection(db, 'timeLogs'), {
        ...newTimeLog,
        hours: isNaN(hours) ? 0 : hours, // Ensure we store a valid number
        userId: currentUser?.uid || null,
        createdAt: new Date(),
        date: dateObj,
      });
      setOpenTimeLogDialog(false);
      setNewTimeLog({
        projectId: '',
        subProjectId: '',
        hours: 0,
        date: new Date().toISOString().split('T')[0],
        description: '',
      });
      fetchTimeLogs();
    } catch (error) {
      console.error('Error creating time log:', error);
    }
  };

  const getClientName = (project) => {
    return project.clientName || 'No Client';
  };

  const getSubProjectsForProject = (projectId) => {
    return subProjects.filter((sp) => sp.projectId === projectId);
  };

  const getLogsForSubProject = (subProjectId) => {
    return timeLogs.filter((log) => log.subProjectId === subProjectId);
  };

  const getTotalHoursForSubProject = (subProjectId) => {
    return timeLogs
      .filter((log) => log.subProjectId === subProjectId)
      .reduce((total, log) => total + log.hours, 0);
  };

  const getBudgetProgress = (subProjectId) => {
    const subProject = subProjects.find((sp) => sp.id === subProjectId);
    if (!subProject || !subProject.budgetHours) return 0;
    const totalHours = getTotalHoursForSubProject(subProjectId);
    return (totalHours / subProject.budgetHours) * 100;
  };

  const handleEditProject = (project) => {
    setEditingProject(project);
    setNewProject({
      name: project.name,
      clientId: project.clientId,
      isNewClient: false,
      isInternal: project.isInternal,
      newClientName: project.clientName,
    });
    setOpenEditProjectDialog(true);
  };

  const handleEditSubProject = (subProject) => {
    setEditingSubProject(subProject);
    setNewSubProject({
      name: subProject.name,
      projectId: subProject.projectId,
      description: subProject.description,
      budgetHours: subProject.budgetHours,
    });
    setOpenEditSubProjectDialog(true);
  };

  const handleEditTimeLog = (log) => {
    setEditingTimeLog(log);

    // Handle different date formats from Firestore
    let formattedDate;
    if (log.date instanceof Date) {
      formattedDate = log.date.toISOString().split('T')[0];
    } else if (log.date?._seconds) {
      // Handle Firestore timestamp
      formattedDate = new Date(log.date._seconds * 1000)
        .toISOString()
        .split('T')[0];
    } else if (log.date?.seconds) {
      // Handle Firestore timestamp (alternative format)
      formattedDate = new Date(log.date.seconds * 1000)
        .toISOString()
        .split('T')[0];
    } else {
      // Fallback to current date if no valid date found
      formattedDate = new Date().toISOString().split('T')[0];
    }

    setNewTimeLog({
      projectId: log.projectId,
      subProjectId: log.subProjectId,
      hours: log.hours,
      date: formattedDate,
      description: log.description,
    });
    setOpenEditTimeLogDialog(true);
  };

  const handleUpdateProject = async () => {
    try {
      const projectData = {
        name: newProject.name,
        clientId: newProject.isInternal ? null : newProject.clientId,
        clientName: newProject.isInternal
          ? 'Internal'
          : newProject.newClientName,
        isInternal: newProject.isInternal,
        updatedAt: new Date(),
        updatedBy: currentUser?.uid || null,
      };

      await updateDoc(doc(db, 'projects', editingProject.id), projectData);
      const updatedProjects = projects.map((p) =>
        p.id === editingProject.id
          ? {
              ...p,
              ...projectData,
            }
          : p
      );
      dispatch(setProjects(updatedProjects));
      setOpenEditProjectDialog(false);
      setEditingProject(null);
    } catch (error) {
      console.error('Error updating project:', error);
    }
  };

  const handleUpdateSubProject = async () => {
    try {
      await updateDoc(doc(db, 'subProjects', editingSubProject.id), {
        name: newSubProject.name,
        description: newSubProject.description,
        budgetHours: newSubProject.budgetHours,
        updatedAt: new Date(),
        updatedBy: currentUser?.uid || null,
      });

      const updatedSubProjects = subProjects.map((sp) =>
        sp.id === editingSubProject.id
          ? {
              ...sp,
              name: newSubProject.name,
              description: newSubProject.description,
              budgetHours: newSubProject.budgetHours,
            }
          : sp
      );
      setSubProjects(updatedSubProjects);
      setOpenEditSubProjectDialog(false);
      setEditingSubProject(null);
    } catch (error) {
      console.error('Error updating sub-project:', error);
    }
  };

  const handleUpdateTimeLog = async () => {
    try {
      const hours = parseFloat(newTimeLog.hours);
      const dateStr = newTimeLog.date;
      const [year, month, day] = dateStr
        .split('-')
        .map((num) => parseInt(num, 10));
      const dateObj = new Date(year, month - 1, day, 12, 0, 0);

      await updateDoc(doc(db, 'timeLogs', editingTimeLog.id), {
        ...newTimeLog,
        hours: isNaN(hours) ? 0 : hours,
        date: dateObj,
        updatedAt: new Date(),
        updatedBy: currentUser?.uid || null,
      });

      const updatedTimeLogs = timeLogs.map((log) =>
        log.id === editingTimeLog.id
          ? {
              ...log,
              hours: isNaN(hours) ? 0 : hours,
              date: dateObj,
              description: newTimeLog.description,
            }
          : log
      );
      setTimeLogs(updatedTimeLogs);
      setOpenEditTimeLogDialog(false);
      setEditingTimeLog(null);
    } catch (error) {
      console.error('Error updating time log:', error);
    }
  };

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 200px)' }}>
      {/* Projects Panel */}
      <Paper sx={{ width: '30%', mr: 2, overflow: 'auto' }}>
        <Box
          sx={{
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6">{t('projects.title')}</Typography>
          </Box>
        </Box>
        <List>
          {(searchQuery ? results.projects : projects).map((project) => (
            <ListItem
              key={project.id}
              button
              selected={selectedProject?.id === project.id}
              onClick={() => {
                setSelectedProject(project);
                setSelectedSubProject(null);
                setSelectedLog(null);
              }}
            >
              <ListItemText
                primary={project.name}
                secondary={`${t('projects.clientName')}: ${getClientName(
                  project
                )}`}
              />
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  aria-label="edit"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditProject(project);
                  }}
                >
                  <EditIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
          {(searchQuery ? results.projects : projects).length === 0 && (
            <ListItem>
              <ListItemText primary={t('projects.noProjectsFound')} />
            </ListItem>
          )}
        </List>
      </Paper>

      {/* Sub-Projects Panel */}
      <Paper sx={{ width: '30%', mr: 2, overflow: 'auto' }}>
        <Box
          sx={{
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6">
            {selectedProject
              ? `${selectedProject.name} - ${t('projects.subProjectDetails')}`
              : t('projects.selectProject')}
          </Typography>
          {selectedProject && (
            <Button
              startIcon={<AddIcon />}
              onClick={() => {
                setNewSubProject({
                  ...newSubProject,
                  projectId: selectedProject.id,
                });
                setOpenSubProjectDialog(true);
              }}
              size="small"
            >
              {t('projects.createSubProject')}
            </Button>
          )}
        </Box>
        {selectedProject ? (
          <List>
            {getSubProjectsForProject(selectedProject.id).map((subProject) => (
              <ListItem
                key={subProject.id}
                button
                selected={selectedSubProject?.id === subProject.id}
                onClick={() => {
                  setSelectedSubProject(subProject);
                  setSelectedLog(null);
                }}
              >
                <ListItemText
                  primary={subProject.name}
                  secondary={subProject.description}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    aria-label="edit"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditSubProject(subProject);
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
            {getSubProjectsForProject(selectedProject.id).length === 0 && (
              <ListItem>
                <ListItemText primary={t('projects.noSubProjects')} />
              </ListItem>
            )}
          </List>
        ) : (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography color="text.secondary">
              {t('projects.selectProject')}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Time Logs Panel */}
      <Paper sx={{ width: '40%', overflow: 'auto' }}>
        <Box
          sx={{
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6">
            {selectedSubProject
              ? `${selectedSubProject.name} - ${t('projects.timeLogDetails')}`
              : t('projects.selectSubProject')}
          </Typography>
          {selectedSubProject && (
            <Button
              startIcon={<AddIcon />}
              onClick={() => {
                setNewTimeLog({
                  ...newTimeLog,
                  projectId: selectedProject.id,
                  subProjectId: selectedSubProject.id,
                });
                setOpenTimeLogDialog(true);
              }}
              size="small"
            >
              {t('projects.logTime')}
            </Button>
          )}
        </Box>
        {selectedSubProject ? (
          <>
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                {t('projects.budgetProgress')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box sx={{ flexGrow: 1, mr: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={getBudgetProgress(selectedSubProject.id)}
                    color={
                      getBudgetProgress(selectedSubProject.id) > 100
                        ? 'error'
                        : 'primary'
                    }
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {getTotalHoursForSubProject(selectedSubProject.id).toFixed(1)}{' '}
                  / {selectedSubProject.budgetHours} {t('projects.hours')}
                </Typography>
              </Box>
            </Box>
            <List>
              {getLogsForSubProject(selectedSubProject.id).map((log) => (
                <ListItem
                  key={log.id}
                  button
                  selected={selectedLog?.id === log.id}
                  onClick={() => setSelectedLog(log)}
                >
                  <ListItemText
                    primary={`${log.hours} ${t('projects.hours')} - ${
                      log.description
                    }`}
                    secondary={moment
                      .unix(log?.date?._seconds || log?.date?.seconds)
                      .format('DD MMM YYYY')}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      aria-label="edit"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditTimeLog(log);
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
              {getLogsForSubProject(selectedSubProject.id).length === 0 && (
                <ListItem>
                  <ListItemText primary={t('projects.noTimeLogs')} />
                </ListItem>
              )}
            </List>
          </>
        ) : (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography color="text.secondary">
              {t('projects.selectSubProject')}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Project Dialog */}
      <Dialog
        open={openProjectDialog}
        onClose={handleCloseProjectDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('projects.createProject')}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="dense">
            <InputLabel>{t('projects.projectType')}</InputLabel>
            <Select
              value={
                newProject.isInternal
                  ? 'internal'
                  : newProject.isNewClient
                  ? 'new'
                  : 'client'
              }
              onChange={(e) => {
                const value = e.target.value;
                if (value === 'internal') {
                  setNewProject({
                    ...newProject,
                    isInternal: true,
                    isNewClient: false,
                    clientId: '',
                  });
                } else if (value === 'new') {
                  setNewProject({
                    ...newProject,
                    isInternal: false,
                    isNewClient: true,
                    clientId: '',
                  });
                } else {
                  setNewProject({
                    ...newProject,
                    isInternal: false,
                    isNewClient: false,
                    clientId: '',
                  });
                }
              }}
            >
              <MenuItem value="internal">
                {t('projects.projectTypeSelect.internal')}
              </MenuItem>
              <MenuItem value="client">
                {t('projects.projectTypeSelect.client')}
              </MenuItem>
            </Select>
          </FormControl>

          {!newProject.isInternal && (
            <FormControl fullWidth margin="dense">
              <InputLabel>{t('projects.clientName')}</InputLabel>
              <Select
                value={newProject.isNewClient ? 'new' : newProject.clientId}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === 'new') {
                    setNewProject({
                      ...newProject,
                      isNewClient: true,
                      clientId: '',
                    });
                  } else {
                    const selectedClient = customers.find(
                      (c) => c.id === value
                    );
                    setNewProject({
                      ...newProject,
                      isNewClient: false,
                      clientId: value,
                      name: selectedClient?.name || '',
                    });
                  }
                }}
                disabled={newProject.isInternal}
              >
                <MenuItem value="new">{t('projects.newClient')}</MenuItem>
                {customers.map((customer) => (
                  <MenuItem key={customer.id} value={customer.id}>
                    {customer.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {newProject.isInternal && (
            <TextField
              margin="dense"
              label={t('projects.projectName')}
              fullWidth
              value={newProject.name}
              onChange={(e) =>
                setNewProject({ ...newProject, name: e.target.value })
              }
            />
          )}

          {newProject.isNewClient && (
            <>
              <TextField
                margin="dense"
                label={t('projects.clientName')}
                fullWidth
                value={newProject.newClientName}
                onChange={(e) =>
                  setNewProject({
                    ...newProject,
                    newClientName: e.target.value,
                  })
                }
              />
              <TextField
                margin="dense"
                label={t('projects.projectName')}
                fullWidth
                value={newProject.name}
                onChange={(e) =>
                  setNewProject({ ...newProject, name: e.target.value })
                }
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseProjectDialog}>
            {t('projects.cancel')}
          </Button>
          <Button
            onClick={handleProjectSubmit}
            disabled={
              newProject.isInternal
                ? !newProject.name
                : newProject.isNewClient
                ? !newProject.name || !newProject.newClientName
                : !newProject.clientId
            }
          >
            {t('projects.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sub-Project Dialog */}
      <Dialog
        open={openSubProjectDialog}
        onClose={() => setOpenSubProjectDialog(false)}
      >
        <DialogTitle>{t('projects.createSubProject')}</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label={t('projects.subProjectName')}
            fullWidth
            value={newSubProject.name}
            onChange={(e) =>
              setNewSubProject({ ...newSubProject, name: e.target.value })
            }
          />
          <TextField
            margin="dense"
            label={t('projects.description')}
            fullWidth
            multiline
            rows={2}
            value={newSubProject.description}
            onChange={(e) =>
              setNewSubProject({
                ...newSubProject,
                description: e.target.value,
              })
            }
          />
          <TextField
            margin="dense"
            label={t('projects.budgetHours')}
            type="number"
            fullWidth
            value={newSubProject.budgetHours}
            onChange={(e) =>
              setNewSubProject({
                ...newSubProject,
                budgetHours: parseFloat(e.target.value),
              })
            }
            InputProps={{
              inputProps: { min: 0, step: 0.5 },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSubProjectDialog(false)}>
            {t('projects.cancel')}
          </Button>
          <Button
            onClick={handleCreateSubProject}
            disabled={!newSubProject.name}
          >
            {t('projects.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Time Log Dialog */}
      <Dialog
        open={openTimeLogDialog}
        onClose={() => setOpenTimeLogDialog(false)}
      >
        <DialogTitle>{t('projects.logTime')}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="dense">
            <InputLabel>{t('projects.projectName')}</InputLabel>
            <Select
              value={newTimeLog.projectId}
              onChange={(e) =>
                setNewTimeLog({ ...newTimeLog, projectId: e.target.value })
              }
            >
              {projects.map((project) => (
                <MenuItem key={project.id} value={project.id}>
                  {project.name} ({getClientName(project)})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>{t('projects.subProjectName')}</InputLabel>
            <Select
              value={newTimeLog.subProjectId}
              onChange={(e) =>
                setNewTimeLog({ ...newTimeLog, subProjectId: e.target.value })
              }
            >
              {subProjects
                .filter((sp) => sp.projectId === newTimeLog.projectId)
                .map((subProject) => (
                  <MenuItem key={subProject.id} value={subProject.id}>
                    {subProject.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label={t('projects.hours')}
            type="number"
            fullWidth
            value={newTimeLog.hours}
            onChange={(e) => {
              const value = e.target.value;
              const hours = value === '' ? 0 : parseFloat(value);
              setNewTimeLog({
                ...newTimeLog,
                hours: hours,
              });
            }}
            InputProps={{
              inputProps: {
                min: 0,
                step: 0.25,
              },
            }}
          />
          <TextField
            margin="dense"
            label={t('projects.date')}
            type="date"
            fullWidth
            value={newTimeLog.date}
            onChange={(e) =>
              setNewTimeLog({ ...newTimeLog, date: e.target.value })
            }
            InputLabelProps={{
              shrink: true,
            }}
          />
          <TextField
            margin="dense"
            label={t('projects.description')}
            fullWidth
            multiline
            rows={2}
            value={newTimeLog.description}
            onChange={(e) =>
              setNewTimeLog({
                ...newTimeLog,
                description: e.target.value,
              })
            }
          />
          <FormControlLabel
            control={
              <Switch
                checked={newTimeLog.isBillable}
                onChange={(e) =>
                  setNewTimeLog({
                    ...newTimeLog,
                    isBillable: e.target.checked,
                  })
                }
              />
            }
            label={t('projects.billable')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTimeLogDialog(false)}>
            {t('projects.cancel')}
          </Button>
          <Button
            onClick={handleCreateTimeLog}
            disabled={
              !newTimeLog.projectId ||
              !newTimeLog.subProjectId ||
              isNaN(parseFloat(newTimeLog.hours)) ||
              parseFloat(newTimeLog.hours) <= 0
            }
          >
            {t('projects.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Project Dialog */}
      <Dialog
        open={openEditProjectDialog}
        onClose={() => {
          setOpenEditProjectDialog(false);
          setEditingProject(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('projects.editProject')}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="dense">
            <InputLabel>{t('projects.projectType')}</InputLabel>
            <Select
              value={
                newProject.isInternal
                  ? 'internal'
                  : newProject.isNewClient
                  ? 'new'
                  : 'client'
              }
              onChange={(e) => {
                const value = e.target.value;
                if (value === 'internal') {
                  setNewProject({
                    ...newProject,
                    isInternal: true,
                    isNewClient: false,
                    clientId: '',
                  });
                } else if (value === 'new') {
                  setNewProject({
                    ...newProject,
                    isInternal: false,
                    isNewClient: true,
                    clientId: '',
                  });
                } else {
                  setNewProject({
                    ...newProject,
                    isInternal: false,
                    isNewClient: false,
                    clientId: '',
                  });
                }
              }}
            >
              <MenuItem value="internal">
                {t('projects.projectTypeSelect.internal')}
              </MenuItem>
              <MenuItem value="client">
                {t('projects.projectTypeSelect.client')}
              </MenuItem>
            </Select>
          </FormControl>

          {!newProject.isInternal && (
            <FormControl fullWidth margin="dense">
              <InputLabel>{t('projects.clientName')}</InputLabel>
              <Select
                value={newProject.isNewClient ? 'new' : newProject.clientId}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === 'new') {
                    setNewProject({
                      ...newProject,
                      isNewClient: true,
                      clientId: '',
                    });
                  } else {
                    const selectedClient = customers.find(
                      (c) => c.id === value
                    );
                    setNewProject({
                      ...newProject,
                      isNewClient: false,
                      clientId: value,
                      name: selectedClient?.name || '',
                    });
                  }
                }}
                disabled={newProject.isInternal}
              >
                <MenuItem value="new">{t('projects.newClient')}</MenuItem>
                {customers.map((customer) => (
                  <MenuItem key={customer.id} value={customer.id}>
                    {customer.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {newProject.isInternal && (
            <TextField
              margin="dense"
              label={t('projects.projectName')}
              fullWidth
              value={newProject.name}
              onChange={(e) =>
                setNewProject({ ...newProject, name: e.target.value })
              }
            />
          )}

          {newProject.isNewClient && (
            <>
              <TextField
                margin="dense"
                label={t('projects.clientName')}
                fullWidth
                value={newProject.newClientName}
                onChange={(e) =>
                  setNewProject({
                    ...newProject,
                    newClientName: e.target.value,
                  })
                }
              />
              <TextField
                margin="dense"
                label={t('projects.projectName')}
                fullWidth
                value={newProject.name}
                onChange={(e) =>
                  setNewProject({ ...newProject, name: e.target.value })
                }
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenEditProjectDialog(false);
              setEditingProject(null);
            }}
          >
            {t('projects.cancel')}
          </Button>
          <Button
            onClick={handleUpdateProject}
            disabled={
              newProject.isInternal
                ? !newProject.name
                : newProject.isNewClient
                ? !newProject.name || !newProject.newClientName
                : !newProject.clientId
            }
          >
            {t('projects.update')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Sub-Project Dialog */}
      <Dialog
        open={openEditSubProjectDialog}
        onClose={() => {
          setOpenEditSubProjectDialog(false);
          setEditingSubProject(null);
        }}
      >
        <DialogTitle>{t('projects.editSubProject')}</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label={t('projects.subProjectName')}
            fullWidth
            value={newSubProject.name}
            onChange={(e) =>
              setNewSubProject({ ...newSubProject, name: e.target.value })
            }
          />
          <TextField
            margin="dense"
            label={t('projects.description')}
            fullWidth
            multiline
            rows={2}
            value={newSubProject.description}
            onChange={(e) =>
              setNewSubProject({
                ...newSubProject,
                description: e.target.value,
              })
            }
          />
          <TextField
            margin="dense"
            label={t('projects.budgetHours')}
            type="number"
            fullWidth
            value={newSubProject.budgetHours}
            onChange={(e) =>
              setNewSubProject({
                ...newSubProject,
                budgetHours: parseFloat(e.target.value),
              })
            }
            InputProps={{
              inputProps: { min: 0, step: 0.5 },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenEditSubProjectDialog(false);
              setEditingSubProject(null);
            }}
          >
            {t('projects.cancel')}
          </Button>
          <Button
            onClick={handleUpdateSubProject}
            disabled={!newSubProject.name}
          >
            {t('projects.update')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Time Log Dialog */}
      <Dialog
        open={openEditTimeLogDialog}
        onClose={() => {
          setOpenEditTimeLogDialog(false);
          setEditingTimeLog(null);
        }}
      >
        <DialogTitle>{t('projects.editTimeLog')}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="dense">
            <InputLabel>{t('projects.projectName')}</InputLabel>
            <Select
              value={newTimeLog.projectId}
              onChange={(e) =>
                setNewTimeLog({ ...newTimeLog, projectId: e.target.value })
              }
              disabled
            >
              {projects.map((project) => (
                <MenuItem key={project.id} value={project.id}>
                  {project.name} ({getClientName(project)})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>{t('projects.subProjectName')}</InputLabel>
            <Select
              value={newTimeLog.subProjectId}
              onChange={(e) =>
                setNewTimeLog({ ...newTimeLog, subProjectId: e.target.value })
              }
              disabled
            >
              {subProjects
                .filter((sp) => sp.projectId === newTimeLog.projectId)
                .map((subProject) => (
                  <MenuItem key={subProject.id} value={subProject.id}>
                    {subProject.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label={t('projects.hours')}
            type="number"
            fullWidth
            value={newTimeLog.hours}
            onChange={(e) => {
              const value = e.target.value;
              const hours = value === '' ? 0 : parseFloat(value);
              setNewTimeLog({
                ...newTimeLog,
                hours: hours,
              });
            }}
            InputProps={{
              inputProps: {
                min: 0,
                step: 0.25,
              },
            }}
          />
          <TextField
            margin="dense"
            label={t('projects.date')}
            type="date"
            fullWidth
            value={newTimeLog.date}
            onChange={(e) =>
              setNewTimeLog({ ...newTimeLog, date: e.target.value })
            }
            InputLabelProps={{
              shrink: true,
            }}
          />
          <TextField
            margin="dense"
            label={t('projects.description')}
            fullWidth
            multiline
            rows={2}
            value={newTimeLog.description}
            onChange={(e) =>
              setNewTimeLog({
                ...newTimeLog,
                description: e.target.value,
              })
            }
          />
          <FormControlLabel
            control={
              <Switch
                checked={newTimeLog.isBillable}
                onChange={(e) =>
                  setNewTimeLog({
                    ...newTimeLog,
                    isBillable: e.target.checked,
                  })
                }
              />
            }
            label={t('projects.billable')}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenEditTimeLogDialog(false);
              setEditingTimeLog(null);
            }}
          >
            {t('projects.cancel')}
          </Button>
          <Button
            onClick={handleUpdateTimeLog}
            disabled={
              !newTimeLog.projectId ||
              !newTimeLog.subProjectId ||
              isNaN(parseFloat(newTimeLog.hours)) ||
              parseFloat(newTimeLog.hours) <= 0
            }
          >
            {t('projects.update')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ProjectList;
